import { useEffect, useRef, useCallback } from 'react';
import { scannerConfig } from '../config/scanner';

export type ScanRejectReason = 'partial' | 'invalid';

export interface UseBarcodeScannerOptions {
  /** Whether the scanner listener is currently active */
  enabled?: boolean;
  /** Callback fired when a valid barcode/QR code scan is recognized */
  onScan: (code: string) => void;
  /** Called when the gun typed something that starts like a CWY code but is cut short or malformed */
  onReject?: (raw: string, reason: ScanRejectReason) => void;
  /** Idle ms after a full code with no Enter before it is emitted */
  idleEmitMs?: number;
  /** Idle ms after which an unfinished code is reported as a partial read */
  partialReadMs?: number;
  /** Max milliseconds between keystrokes to be considered scanner input (default: 50ms) */
  maxIntervalMs?: number;
  /** Minimum characters for a scan buffer (default: 20) */
  minChars?: number;
  /** Prefix required to match CWY QR codes (default: 'CWY1.') */
  prefix?: string;
  /** Play an audio beep on scan detection. Off by default; the scanner gun already beeps. */
  playBeep?: boolean;
}

/**
 * Synthesizes a crisp electronic scanner confirmation beep via Web Audio API.
 */
function playScannerBeep() {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime); // A5 note
    osc.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.08); // chirp up to A6

    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.1);
  } catch {
    // Ignore audio context errors if browser audio is not permitted
  }
}

/** A finished pass is CWY1.client.time.signature, or the same with an office before the signature. */
export function isCompleteCheckInCode(raw: string): boolean {
  const code = raw.trim().split('@')[0].trim().toUpperCase();
  const parts = code.split('.');
  if (parts[0] !== 'CWY1' || (parts.length !== 4 && parts.length !== 5)) return false;
  const signature = parts[parts.length - 1];
  return signature.length === 20 && parts.every((part) => /^[0-9A-Z]+$/.test(part));
}

/** Pull a finished check-in code out of scanner text that landed in a field. */
export function extractCheckInCode(raw: string): string | null {
  const match = raw.toUpperCase().match(/CWY1\.(?:[0-9A-Z]+\.){2,3}[0-9A-Z]{20}/);
  if (!match || !isCompleteCheckInCode(match[0])) return null;
  return match[0];
}

/** True while the field only has the start of a CWY check-in code (C, CW, CWY1, …). */
export function isCwyPrefixFragment(value: string, prefix = scannerConfig.prefix): boolean {
  const upper = value.trim().toUpperCase();
  if (!upper) return false;
  return prefix.toUpperCase().startsWith(upper);
}

function shouldSwallowScannerKey(
  nextBuffer: string,
  timeSinceLastKey: number,
  prefix: string,
  maxIntervalMs: number
): boolean {
  const upper = nextBuffer.toUpperCase();
  const expected = prefix.toUpperCase();
  if (upper.startsWith(expected)) return true;
  return expected.startsWith(upper) && upper.length >= 2 && timeSinceLastKey <= maxIntervalMs * 2;
}

function stripScanFromFocusedField(target: EventTarget | null, code: string, prefix: string) {
  if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) return;
  if (target.getAttribute('data-scanner-field') === 'true') {
    target.value = '';
    return;
  }
  const value = target.value;
  if (!value) return;
  const upper = value.toUpperCase();
  const codeUpper = code.toUpperCase();
  let next = value;
  if (upper.includes(codeUpper)) {
    const idx = upper.indexOf(codeUpper);
    next = value.slice(0, idx) + value.slice(idx + code.length);
  }
  const leftover = next.toUpperCase();
  if (!leftover || leftover.includes('CWY1') || prefix.toUpperCase().startsWith(leftover)) {
    next = '';
  }
  if (next === value) return;
  target.value = next;
}

/**
 * Global keyboard listener hook for hardware USB barcode/QR scanners operating in HID keyboard mode.
 * USB scanners emulate keyboard typing and usually end with Enter. Some guns omit Enter.
 */
export function useBarcodeScanner({
  enabled = true,
  onScan,
  onReject,
  maxIntervalMs = scannerConfig.maxIntervalMs,
  minChars = scannerConfig.minChars,
  prefix = scannerConfig.prefix,
  playBeep = scannerConfig.playBeep,
  idleEmitMs = scannerConfig.idleEmitMs,
  partialReadMs = scannerConfig.partialReadMs,
}: UseBarcodeScannerOptions) {
  const bufferRef = useRef<string>('');
  const lastKeyTimeRef = useRef<number>(0);
  const scanTimestampsRef = useRef<number[]>([]);
  const idleTimerRef = useRef<number | null>(null);
  const partialTimerRef = useRef<number | null>(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const onRejectRef = useRef(onReject);
  onRejectRef.current = onReject;

  const clearIdle = () => {
    if (idleTimerRef.current != null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const clearPartial = () => {
    if (partialTimerRef.current != null) {
      window.clearTimeout(partialTimerRef.current);
      partialTimerRef.current = null;
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      const now = Date.now();
      const timeSinceLastKey = now - lastKeyTimeRef.current;
      lastKeyTimeRef.current = now;

      const emitScan = () => {
        const bufferedText = bufferRef.current.trim().split('@')[0].trim();
        const timestamps = scanTimestampsRef.current;
        bufferRef.current = '';
        scanTimestampsRef.current = [];
        clearIdle();
        clearPartial();

        const startsWithPrefix = bufferedText.toUpperCase().startsWith(prefix.toUpperCase());
        const hasValidLength = bufferedText.length >= minChars;
        let isScannerSpeed = true;
        if (timestamps.length >= 4) {
          const intervals: number[] = [];
          for (let i = 1; i < timestamps.length; i++) {
            intervals.push(timestamps[i] - timestamps[i - 1]);
          }
          const avgInterval = intervals.reduce((sum, val) => sum + val, 0) / intervals.length;
          isScannerSpeed = avgInterval <= maxIntervalMs * 1.5;
        }
        if (!startsWithPrefix || (!hasValidLength && !isScannerSpeed)) return;
        if (!isCompleteCheckInCode(bufferedText)) {
          onRejectRef.current?.(bufferedText, 'invalid');
          return;
        }

        stripScanFromFocusedField(event.target, bufferedText, prefix);

        if (playBeep) playScannerBeep();
        onScanRef.current(bufferedText.toUpperCase());
      };

      // Handle Enter (scanner terminating character)
      if (event.key === 'Enter') {
        const bufferedText = bufferRef.current.trim().split('@')[0].trim();
        const startsWithPrefix = bufferedText.toUpperCase().startsWith(prefix.toUpperCase());
        if (startsWithPrefix && bufferedText.length >= minChars) {
          event.preventDefault();
          event.stopPropagation();
        }
        emitScan();
        return;
      }

      // Ignore special modifier keys
      if (
        event.key === 'Shift' ||
        event.key === 'Control' ||
        event.key === 'Alt' ||
        event.key === 'Meta' ||
        event.key === 'Tab' ||
        event.key === 'Escape'
      ) {
        return;
      }

      // If too much time has passed between keystrokes and buffer doesn't look like an ongoing scanner sequence,
      // reset buffer to avoid accumulating stale manual typing
      if (timeSinceLastKey > 400 && !bufferRef.current.toUpperCase().startsWith(prefix.toUpperCase())) {
        bufferRef.current = '';
        scanTimestampsRef.current = [];
      }

      // Append printable single character
      if (event.key.length === 1) {
        const nextBuffer = bufferRef.current + event.key;
        if (shouldSwallowScannerKey(nextBuffer, timeSinceLastKey, prefix, maxIntervalMs)) {
          event.preventDefault();
          event.stopPropagation();
        }
        clearIdle();
        clearPartial();
        bufferRef.current += event.key;
        scanTimestampsRef.current.push(now);
        if (isCompleteCheckInCode(bufferRef.current)) {
          if (idleEmitMs <= 0) {
            emitScan();
          } else {
            idleTimerRef.current = window.setTimeout(() => {
              idleTimerRef.current = null;
              if (isCompleteCheckInCode(bufferRef.current)) emitScan();
            }, idleEmitMs);
          }
        } else if (bufferRef.current.toUpperCase().startsWith(prefix.toUpperCase())) {
          // Started like a CWY code but never finished: the gun misread or keystrokes were lost.
          partialTimerRef.current = window.setTimeout(() => {
            partialTimerRef.current = null;
            const partial = bufferRef.current.trim();
            if (!partial || isCompleteCheckInCode(partial)) return;
            bufferRef.current = '';
            scanTimestampsRef.current = [];
            onRejectRef.current?.(partial, 'partial');
          }, partialReadMs);
        }
      }
    },
    [enabled, maxIntervalMs, minChars, prefix, playBeep, idleEmitMs, partialReadMs]
  );

  useEffect(() => {
    if (!enabled) return;

    const handleBeforeInput = (event: Event) => {
      const inputEvent = event as InputEvent;
      const data = inputEvent.data || '';
      if (!data) return;
      const next = `${bufferRef.current}${data}`;
      if (shouldSwallowScannerKey(next, Date.now() - lastKeyTimeRef.current, prefix, maxIntervalMs)) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('beforeinput', handleBeforeInput, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('beforeinput', handleBeforeInput, true);
      if (idleTimerRef.current != null) window.clearTimeout(idleTimerRef.current);
      if (partialTimerRef.current != null) window.clearTimeout(partialTimerRef.current);
    };
  }, [enabled, handleKeyDown, prefix, maxIntervalMs]);

  return {
    playBeep: playScannerBeep,
  };
}
